'use client';

import React, { useState, useRef } from 'react';
import dynamic from 'next/dynamic';

import { useTreatmentApiContext } from '../TreatmentApiContext/TreatmentApiContext';
import useTreatmentPageSelectorAnalytics from './hooks/useTreatmentPageSelectorAnalytics';
import getPercentageSaving from '../TreatmentSelector/hooks/useFormatQuantitiesForDisplay/helpers/getPercentageSaving';
import useStartConsultation from '../../Consultation/hooks/useStartConsultation';
import { bloomreachPixelSendProductView } from '../../../modules/analytics/bloomreach/BloomreachPixel/sendBloomreachPageViewTrackingEvent';
import { sanityBlockContentToString } from '../../../helpers/text/sanityBlockContentToString';

import TreatmentSelector, { SelectorOnChangeData, SelectorOnSubmitData } from '../TreatmentSelector/TreatmentSelector';
import Incentives from '../TreatmentSelector/Incentives/Incentives';
import LoadingSkeleton from '../TreatmentSelector/LoadingSkeleton/LoadingSkeleton';
import ImageWithFallback from '../../NextJs/ImageWithFallback/ImageWithFallback';
import ReasonsToBuy, { ReasonsToBuyFields } from './ReasonsToBuy/ReasonsToBuy';

import styles from './TreatmentPageSelector.module.css';

const TreatmentSelectorComponent = dynamic(() => import('../TreatmentSelector/TreatmentSelector'), {
    ssr: false,
    loading: LoadingSkeleton,
});

type TreatmentSelectorHeaderProps = React.ComponentProps<typeof TreatmentSelector.Header>;
type TreatmentSelectorNotificationsProps = React.ComponentProps<typeof TreatmentSelector.Notifications>;

interface Props {
    initialImageSrc: string;
    title: TreatmentSelectorHeaderProps['title'];
    review: TreatmentSelectorHeaderProps['review'];
    fromPrice: TreatmentSelectorHeaderProps['fromPrice'];
    conditionName: TreatmentSelectorNotificationsProps['conditionName'];
    conditionSlug: string;
    isPrivatePrescription?: TreatmentSelectorNotificationsProps['isPrivatePrescription'];
    gppcFromPrice?: TreatmentSelectorHeaderProps['gppcFromPrice'];
    reasons: ReasonsToBuyFields['reasons'];
    className?: string;
}

const TreatmentPageSelector = ({
    initialImageSrc,
    title,
    review,
    fromPrice,
    conditionName,
    conditionSlug,
    isPrivatePrescription,
    gppcFromPrice,
    reasons,
    className,
}: Props) => {
    // TODO: Have put this for bloomreach tracking. in next js we can probably move this
    const trackingHasRun = useRef(false);

    const { isLoading, error, treatment } = useTreatmentApiContext();
    const startConsultation = useStartConsultation();

    useTreatmentPageSelectorAnalytics(treatment);

    const [details, setDetails] = useState({
        imgSrc: initialImageSrc,
        percentageSaving: 0,
    });

    const handleSubmit = ({ variant, quantity }: SelectorOnSubmitData) => {
        startConsultation(conditionSlug, {
            conditionId: treatment!.condition_id,
            treatmentId: variant.id,
            quantity: quantity.quantity,
            selectedVariant: variant,
            selectedQuantityInfo: quantity,
            selectedTreatment: treatment!,
        });
    };

    const handleOnChange = ({ variant }: SelectorOnChangeData) => {
        let imgSrc = initialImageSrc;

        if (variant.public && Array.isArray(variant.public) === false) {
            imgSrc = variant.public.images.mediumFeaturedUrl || imgSrc;
        }

        // Grab the treatment parent id other wise use the current treatment id, other wise use the variant id.
        const prodId = (treatment?.parent && treatment?.parent?.reference? treatment.parent.reference : treatment?.id) || variant.id;

        if (trackingHasRun.current === false) {
            bloomreachPixelSendProductView({
                prod_id: prodId.toString(),
                prod_name: variant.name,
                sku: variant.id.toString(), // Product ID - (@Phil mentioned we should just use the id as the sku)
            });
            trackingHasRun.current = true;
        }

        setDetails({
            imgSrc,
            percentageSaving: getPercentageSaving(variant.quantities.sort((a, b) => a.quantity - b.quantity)),
        });
    };

    const scrollToSelector = review?.numRatings ? '#treatment-reviews' : undefined;

    const inStock = treatment?.variants.some((variant) => !!variant.quantities.length) || false;

    return (
        <div className={`${styles.grid} ${className || ''}`}>
            <ImageWithFallback
                fallback="/images/logo-dark.svg"
                src={details.imgSrc}
                alt={sanityBlockContentToString(title)}
                className={styles.image}
                priority
                width={280}
                height={280}
            />
            <TreatmentSelector.Header
                headingTag="h1"
                title={title}
                review={review}
                fromPrice={fromPrice}
                scrollToSelector={scrollToSelector}
                percentageSaving={details.percentageSaving}
                gppcFromPrice={gppcFromPrice}
                className={styles.header}
            />
            {reasons?.length ? <ReasonsToBuy reasons={reasons} className={styles.reasonsToBuy} /> : null}
            {/* Wrapped in div to avoid elements inside growing to the grid height. */}
            <div className={styles.treatmentSelector}>
                <TreatmentSelectorComponent
                    onSubmit={handleSubmit}
                    onChange={handleOnChange}
                    treatment={treatment}
                    isError={!!error}
                    isLoading={isLoading}
                    className="space-y-150"
                >
                    <TreatmentSelector.VariantSelector />
                    <TreatmentSelector.QuantitySelector />
                    <TreatmentSelector.Notifications
                        conditionName={conditionName}
                        isPrivatePrescription={isPrivatePrescription}
                    />
                    <TreatmentSelector.TreatmentPageButton
                        conditionSlug={conditionSlug}
                        className="w-full text-center"
                        loading={false}
                    />
                </TreatmentSelectorComponent>
            </div>
            <Incentives hideDeliveryEstimate={!!error} inStock={inStock} className={styles.incentives} />
        </div>
    );
};

export default TreatmentPageSelector;
